import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import {
  sanitizeInput,
  sanitizeEmail,
  sanitizeAlphanumeric,
  validateUsername,
  validatePassword,
  isValidEmail,
  validateInput,
} from "../../utils/inputValidation";
import PasswordStrengthIndicator from "./PasswordStrengthIndicator";

// Icon components
const EyeIcon = ({ className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    fill="none"
    viewBox="0 0 24 24"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M1.5 12s3.5-7 10.5-7 10.5 7 10.5 7-3.5 7-10.5 7S1.5 12 1.5 12Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const EyeOffIcon = ({ className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    fill="none"
    viewBox="0 0 24 24"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M3 3l18 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9.88 9.88A3.5 3.5 0 0 0 12 8.5c1.93 0 3.5 1.57 3.5 3.5 0 .75-.24 1.45-.65 2.02"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.11 6.11C3.86 7.68 2.4 9.7 1.5 12c0 0 3.5 7 10.5 7 2.15 0 4-.47 5.61-1.26M14.12 14.12A3.5 3.5 0 0 1 12 15.5c-1.93 0-3.5-1.57-3.5-3.5 0-.46.09-.9.26-1.3"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M22.5 12s-1.05-2.13-3.21-3.98"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Register = ({ role: forcedRole, endpoint: forcedEndpoint }) => {
  const [formData, setFormData] = useState({
    username: "",
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    professionType: "",
    practiceInstructorDepartment: "",
    startYear: new Date().getFullYear().toString(),
    role: forcedRole || "LERNENDE",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  const { register, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (formData.firstName && formData.lastName) {
      const cleanLastName = formData.lastName.toLowerCase().replace(/\s+/g, "");
      const cleanFirstName = formData.firstName
        .toLowerCase()
        .replace(/\s+/g, "");

      setFormData((prev) => ({
        ...prev,
        username: cleanLastName,
        email: `${cleanFirstName}.${cleanLastName}@sunrise.net`,
      }));
    }
  }, [formData.firstName, formData.lastName]);

  const generatePassword = () => {
    const lowercase = "abcdefghijklmnopqrstuvwxyz";
    const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const numbers = "0123456789";
    const symbols = "!@#$%^&*";

    let password = "";
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];

    const allChars = lowercase + uppercase + numbers + symbols;
    for (let i = 4; i < 12; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }

    password = password
      .split("")
      .sort(() => Math.random() - 0.5)
      .join("");

    setFormData((prev) => ({
      ...prev,
      password,
      confirmPassword: password,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name === "username") {
      processedValue = sanitizeAlphanumeric(value);
    } else if (name === "email") {
      processedValue = sanitizeEmail(value);
    } else if (name === "firstName" || name === "lastName") {
      processedValue = sanitizeInput(value);
    } else if (name === "practiceInstructorDepartment") {
      processedValue = sanitizeInput(value);
    } else if (name === "startYear") {
      processedValue = value.replace(/\D/g, "");
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handlePasswordBlur = () => {
    setPasswordFocused(false);
    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      setErrors((prev) => ({
        ...prev,
        password: passwordValidation.errors[0],
      }));
    }
  };

  const handleConfirmPasswordBlur = () => {
    if (formData.password !== formData.confirmPassword) {
      setErrors((prev) => ({
        ...prev,
        confirmPassword: "Passwords do not match",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    } else if (formData.firstName.length > 100) {
      newErrors.firstName = "First name must not exceed 100 characters";
    } else {
      const validation = validateInput(formData.firstName);
      if (!validation.isValid) {
        newErrors.firstName = "First name contains invalid characters";
      }
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    } else if (formData.lastName.length > 100) {
      newErrors.lastName = "Last name must not exceed 100 characters";
    } else {
      const validation = validateInput(formData.lastName);
      if (!validation.isValid) {
        newErrors.lastName = "Last name contains invalid characters";
      }
    }

    const usernameValidation = validateUsername(formData.username);
    if (!usernameValidation.isValid) {
      newErrors.username = usernameValidation.error;
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    } else if (formData.email.length > 255) {
      newErrors.email = "Email must not exceed 255 characters";
    }

    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      newErrors.password = passwordValidation.errors[0];
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (formData.role === "LERNENDE" && !formData.professionType) {
      newErrors.professionType = "Please select a profession type";
    }

    if (
      formData.role === "PRACTICE_INSTRUCTOR" &&
      !formData.practiceInstructorDepartment.trim()
    ) {
      newErrors.practiceInstructorDepartment =
        "Please enter the department or team";
    }

    if (formData.role === "LERNENDE") {
      if (
        !formData.startYear ||
        formData.startYear < 2000 ||
        formData.startYear > 2100
      ) {
        newErrors.startYear = "Please enter a valid start year";
      }
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const registerPayload = {
        username: formData.username,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        professionType: formData.professionType,
        practiceInstructorDepartment: formData.practiceInstructorDepartment,
        roles: [formData.role],
      };

      if (formData.role === "LERNENDE") {
        registerPayload.classStartDate = `${formData.startYear}-08-01`;
      }

      const targetEndpoint = forcedEndpoint || "/register";
      const registerResult = await register(registerPayload, targetEndpoint);

      if (registerResult.success) {
        setSuccess(true);
        setTimeout(async () => {
          const loginResult = await login({
            usernameOrEmail: formData.username,
            password: formData.password,
          });

          if (loginResult.success) {
            navigate("/");
          } else {
            navigate("/login");
          }
        }, 2000);
      } else {
        setErrors({ submit: registerResult.message });
      }
    } catch (error) {
      setErrors({ submit: "Registration failed. Please try again." });
    }

    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#99b2a7] to-[#f8edd2] flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="card-brand p-8 animate-fade-in text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg
                className="w-12 h-12 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                ></path>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-green-600 mb-4">
              Registration Successful
            </h2>
            <p className="text-gray-600 mb-8">
              Your account has been created successfully. You will now be
              redirected.
            </p>
            <div className="flex justify-center">
              <svg
                className="animate-spin h-8 w-8 text-sunrise-red"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-green-cloud flex items-center justify-center px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-2xl w-full">
        <div className="card-brand p-8 sm:p-10 animate-fade-in">
          <div className="text-center mb-8">
            <img
              src="/Journey_Logo.png"
              alt="Journey Logo"
              className="mx-auto mb-4 h-28 sm:h-32 object-contain"
            />
            <h2 className="text-2xl font-bold text-grey-dusk mb-2">
              Create Account
            </h2>
          </div>

          {errors.submit && (
            <div className="alert-brand-error mb-6">{errors.submit}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="label-brand">
                  First Name
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={`input-brand ${errors.firstName ? "input-brand-error" : ""}`}
                  disabled={loading}
                  placeholder="Enter your first name"
                />
                {errors.firstName && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.firstName}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="lastName" className="label-brand">
                  Last Name
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={`input-brand ${errors.lastName ? "input-brand-error" : ""}`}
                  disabled={loading}
                  placeholder="Enter your last name"
                />
                {errors.lastName && (
                  <p className="text-sm text-red-600 mt-1">{errors.lastName}</p>
                )}
              </div>
            </div>
            {/* Username */}{" "}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="username" className="label-brand">
                  Username
                </label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  className={`input-brand ${errors.username ? "input-brand-error" : ""}`}
                  disabled={loading}
                  placeholder="Generated from your name"
                />
                {errors.username && (
                  <p className="text-sm text-red-600 mt-1">{errors.username}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="label-brand">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`input-brand ${errors.email ? "input-brand-error" : ""}`}
                  disabled={loading}
                  placeholder="Generated from your name"
                />
                {errors.email && (
                  <p className="text-sm text-red-600 mt-1">{errors.email}</p>
                )}
              </div>
            </div>
            {/* Learner fields */}
            {formData.role === "LERNENDE" && (
              <>
                <div>
                  <label htmlFor="professionType" className="label-brand">
                    Profession Type
                  </label>
                  <select
                    id="professionType"
                    name="professionType"
                    value={formData.professionType}
                    onChange={handleChange}
                    className={`input-brand ${errors.professionType ? "input-brand-error" : ""}`}
                    disabled={loading}
                  >
                    <option value="">Select your profession type</option>
                    <option value="INFORMATIK">Informatik</option>
                    <option value="MEDIAMATIK">Mediamatik</option>
                    <option value="KV">KV</option>
                    <option value="KUNDENDIALOG">Kundendialog</option>
                    <option value="DETAILHANDEL">Detailhandel</option>
                  </select>
                  {errors.professionType && (
                    <p className="text-sm text-red-600 mt-1">
                      {errors.professionType}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="startYear" className="label-brand">
                    Apprenticeship Start Year
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    id="startYear"
                    name="startYear"
                    maxLength="4"
                    placeholder="YYYY"
                    value={formData.startYear}
                    onChange={handleChange}
                    className={`input-brand ${errors.startYear ? "input-brand-error" : ""}`}
                    disabled={loading}
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Enter the year you started (e.g. {new Date().getFullYear()}
                    ). We automatically set the date to August 1st.
                  </p>
                  {errors.startYear && (
                    <p className="text-sm text-red-600 mt-1">
                      {errors.startYear}
                    </p>
                  )}
                </div>
              </>
            )}

            {formData.role === "PRACTICE_INSTRUCTOR" && (
              <div>
                <label htmlFor="practiceInstructorDepartment" className="label-brand">
                  Department
                </label>
                <input
                  type="text"
                  id="practiceInstructorDepartment"
                  name="practiceInstructorDepartment"
                  value={formData.practiceInstructorDepartment}
                  onChange={handleChange}
                  className={`input-brand ${errors.practiceInstructorDepartment ? "input-brand-error" : ""}`}
                  disabled={loading}
                  placeholder="e.g. Network Security"
                />
                {errors.practiceInstructorDepartment && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.practiceInstructorDepartment}
                  </p>
                )}
              </div>
            )}
            {/* Password row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="password" className="label-brand">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    onFocus={() => setPasswordFocused(true)}
                    onBlur={handlePasswordBlur}
                    className={`input-brand pr-12 ${errors.password ? "input-brand-error" : ""}`}
                    disabled={loading}
                    placeholder="Create a strong password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                    className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700 focus:outline-none"
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? (
                      <EyeOffIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>

                {passwordFocused && formData.password.length > 0 && (
                  <div className="mt-2">
                    <PasswordStrengthIndicator password={formData.password} />
                  </div>
                )}

                {errors.password && !passwordFocused && (
                  <p className="text-sm text-red-600 mt-1">{errors.password}</p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="label-brand">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    onBlur={handleConfirmPasswordBlur}
                    className={`input-brand pr-12 ${errors.confirmPassword ? "input-brand-error" : ""}`}
                    disabled={loading}
                    placeholder="Confirm your password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex={-1}
                    className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700 focus:outline-none"
                    aria-label={
                      showConfirmPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showConfirmPassword ? (
                      <EyeOffIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>

                {errors.confirmPassword && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.confirmPassword}
                  </p>
                )}
              </div>
            </div>
            {/* Generate password */}
            <div className="flex justify-center">
              <button
                type="button"
                onClick={generatePassword}
                className="px-5 py-2.5 rounded-xl border border-pale-dawn bg-gradient-to-r from-green-cloud/70 to-pale-dawn text-white font-semibold shadow-sm hover:shadow-md hover:opacity-95 transition-all"
                disabled={loading}
              >
                <i className="fas fa-wand-magic-sparkles mr-2"></i>
                Generate Secure Password
              </button>
            </div>
            <input type="hidden" name="role" value={formData.role} />
            {/* Submit */}
            <div className="pt-1">
              <button
                type="submit"
                className={`w-full rounded-xl bg-sunrise-red text-white font-semibold px-5 py-3.5 shadow-sm hover:bg-sunrise-600 hover:shadow-md transition-all duration-200 ${
                  loading ? "opacity-75 cursor-not-allowed" : ""
                }`}
                disabled={loading}
              >
                {loading ? "Creating Account..." : "Create Account"}
              </button>
            </div>
          </form>

          <div className="mt-6 pt-6 border-t border-pale-dawn text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{" "}
              <Link
                to="/login"
                className="font-semibold text-sunrise-red hover:text-sunrise-600 transition-colors duration-200"
              >
                Sign in here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
